import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Eye, Sparkles, Check, Shield } from 'lucide-react';
import NCAAService from '../../../../../components/dashboards/services/ncaaService';
import { useAddToChat } from '../../../../../components/chat/chatContext';
import PredictButton from '../../../../../components/dashboards/components/shared/PredictButton';
import XPicksButton from '../../../../../components/dashboards/components/shared/XPicksButton';
import ViewOddsButton from '../../../../../components/dashboards/components/shared/ViewOddsButton';
import PredictionBox from '../../../../../components/dashboards/components/shared/PredictionBox';

const NCAAFDesktopGameCard = memo(({
  game,
  isSelected,
  isFrozen = false,
  onToggleSelection,
  prediction,
  onGameSelect,
  onViewOdds
}) => {
  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  // Handler to stage prediction and navigate to chat
  const handleAnalyzeInChat = async () => {
    if (!prediction) return;

    const message = `Analyze this NCAAF prediction:

${teams?.away?.name} @ ${teams?.home?.name}

Prediction: ${prediction.winner}${prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% confidence)` : ''}
Confidence Level: ${prediction.confidence}
${prediction.reason ? `Reasoning: ${prediction.reason}` : ''}

Can you provide a deeper analysis of this matchup?`;

    await handleAddToChat(message);
    navigate('/chat');
  };

  if (!game) return null;

  const { teams, scores, game: gameInfo } = game;
  // Check for various live status codes including quarters
  const statusShort = gameInfo?.status?.short;
  const isLive = statusShort === 'IN PLAY' || statusShort === 'LIVE' || 
                 statusShort === 'HT' || // Halftime
                 (statusShort && statusShort.startsWith('Q')) || // Q1, Q2, Q3, Q4
                 statusShort === 'OT'; // Overtime
  const isFinished = statusShort === 'FT' || statusShort === 'AOT';
  const isUpcoming = !isLive && !isFinished;
  
  // Format game time to ET
  const formatGameTime = () => {
    if (isLive) return 'LIVE';
    if (isFinished) return 'Final';
    
    // NCAAF games have separate date and time fields
    if (gameInfo?.date?.date && gameInfo?.date?.time) {
      try {
        // Combine date and time fields
        const dateTimeString = `${gameInfo.date.date}T${gameInfo.date.time}`;
        const formattedTime = NCAAService.formatTime(dateTimeString);
        // formatTime already includes ET, don't double it
        return formattedTime;
      } catch (e) {
        return 'TBD';
      }
    } else if (gameInfo?.date) {
      try {
        // Fallback if date is already a combined string
        const formattedTime = NCAAService.formatTime(gameInfo.date);
        // formatTime already includes ET, don't double it
        return formattedTime;
      } catch (e) {
        return 'TBD';
      }
    }
    
    return 'TBD';
  };

  // Get quarter display for live games
  const getQuarterDisplay = () => {
    if (!isLive) return null;
    
    // Check if status itself contains quarter info (Q1, Q2, etc)
    if (statusShort && statusShort.startsWith('Q')) {
      return statusShort;
    }
    
    // Check for halftime
    if (statusShort === 'HT') {
      return 'HT';
    }
    
    // Check for overtime
    if (statusShort === 'OT') {
      return 'OT';
    }
    
    // Fallback to quarter field if available
    const quarter = gameInfo?.status?.quarter;
    if (quarter) {
      return `Q${quarter}`;
    }
    
    return 'In Progress';
  };

  // Check if it's a ranked team
  const getRanking = (teamName) => {
    // NCAA rankings are often in the team name like "#1 Georgia"
    const match = teamName?.match(/^#(\d+)\s/);
    return match ? match[1] : null;
  };

  // Clean team name (remove ranking prefix if present)
  const cleanTeamName = (teamName) => {
    return teamName?.replace(/^#\d+\s/, '') || 'Unknown';
  };

  return (
    <div className={`relative bg-white/[0.03] rounded-lg p-4 hover:bg-white/[0.05] transition-all duration-200 border ${
      isSelected ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-white/10 hover:border-white/20'
    } active:bg-white/[0.07]`}>
      {/* Status Badge - Top Right */}
      {(isLive || isFinished) && (
        <div className="absolute top-3 right-3 z-10">
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${
            isLive ? 'bg-red-500' : 'bg-white/20'
          }`}>
            {isLive && <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />}
            <span className="text-[10px] font-bold text-white">
              {isLive ? (getQuarterDisplay() ? `LIVE ${getQuarterDisplay()}` : 'LIVE') : 'FT'}
            </span>
          </div>
        </div>
      )}
      
      {/* League/Conference Label */}
      <div className="text-xs text-white/65 font-semibold uppercase tracking-wider mb-3 truncate pr-16">
        {teams?.home?.conference || teams?.away?.conference || 'NCAA'}
      </div>
        
      {/* Teams and Scores - Vertical Layout */}
      <div className="space-y-3">
        {/* Home Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {teams?.home?.logo ? (
              <img 
                src={teams.home.logo} 
                alt={teams.home.name}
                className="w-10 h-10 object-contain flex-shrink-0"
                loading="lazy"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 rounded-lg flex items-center justify-center flex-shrink-0 border border-emerald-500/30">
                <Shield className="w-5 h-5 text-emerald-400" />
              </div>
            )}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {getRanking(teams?.home?.name) && (
                <span className="text-xs font-bold text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded flex-shrink-0">
                  #{getRanking(teams?.home?.name)}
                </span>
              )}
              <span className="text-sm font-semibold text-white truncate">
                {cleanTeamName(teams?.home?.name) || 'Home Team'}
              </span>
            </div>
          </div>
          <span className="text-xl font-black text-white ml-3 tabular-nums">
            {isFinished || isLive ? (scores?.home?.total ?? scores?.home ?? 0) : '-'}
          </span>
        </div>

        {/* Away Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {teams?.away?.logo ? (
              <img 
                src={teams.away.logo} 
                alt={teams.away.name}
                className="w-10 h-10 object-contain flex-shrink-0"
                loading="lazy"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0 border border-blue-500/30">
                <Shield className="w-5 h-5 text-blue-400" />
              </div>
            )}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {getRanking(teams?.away?.name) && (
                <span className="text-xs font-bold text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded flex-shrink-0">
                  #{getRanking(teams?.away?.name)}
                </span>
              )}
              <span className="text-sm font-semibold text-white truncate">
                {cleanTeamName(teams?.away?.name) || 'Away Team'}
              </span>
            </div>
          </div>
          <span className="text-xl font-black text-white ml-3 tabular-nums">
            {isFinished || isLive ? (scores?.away?.total ?? scores?.away ?? 0) : '-'}
          </span>
        </div>
      </div>

      {/* Game Time - only for upcoming games */}
      {!isFinished && !isLive && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="flex items-center justify-center gap-1 text-white/50">
            <Clock size={12} />
            <span className="text-xs font-medium">
              {formatGameTime()}
            </span>
          </div>
        </div>
      )}

      {/* Prediction Result (if available) */}
      {prediction && (
        <PredictionBox
          prediction={prediction}
          onAnalyze={handleAnalyzeInChat}
          variant="desktop" />
      )}
      
      {/* Action Buttons */}
      <div className="mt-3 grid grid-cols-3 gap-2 items-stretch">
        {/* xPicks Button */}
        {(isUpcoming || isLive) && (
          <div className="col-span-1">
            <XPicksButton
              gameData={{
                homeTeam: cleanTeamName(teams?.home?.name),
                awayTeam: cleanTeamName(teams?.away?.name),
                homeTeamId: teams?.home?.id,
                awayTeamId: teams?.away?.id,
                leagueId: game?.league?.id || 2,
                date: gameInfo?.date?.date || gameInfo?.date,
                time: formatGameTime(),
                gameId: gameInfo?.id
              }}
              sport="ncaaf"
              label="XPICKS"
            />
          </div>
        )}

      {/* Prediction Button */}
      {(isUpcoming || isLive) && onToggleSelection && (
        <div className="col-span-1">
          <PredictButton
            label={isFrozen ? "COOLDOWN" : isSelected ? "SELECTED" : isLive ? "PREDICT LIVE" : "PREDICT"}
            icon={isFrozen ? Clock : isSelected ? Check : Sparkles}
            isSelected={isSelected}
            isLive={isLive}
            isFrozen={isFrozen}
            onClick={(e) => {
              e.stopPropagation();
              if (!isFrozen) {
                onToggleSelection(game);
              }
            }}
          />
        </div>
      )}

        {/* View Odds Button */}
        {onViewOdds && (
          <div className="col-span-1">
            <ViewOddsButton
              label="VIEW ODDS"
              icon={Eye}
              onClick={() => onViewOdds(game)}
            />
          </div>
        )}
      </div>
    </div>
  );
});

NCAAFDesktopGameCard.displayName = 'NCAAFGameCardWithPrediction';

export default NCAAFDesktopGameCard;
